import {
  defaultStageForSignalStatus,
  isAllowedStageStatusCombination,
  SignalStage,
  SignalStatus,
  type SignalStageType,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';

export type SignalRelatedFindingRef = {
  id: string;
  title?: string | null;
};

export const FALSE_POSITIVE_PREVIOUS_STATUSES = new Set<SignalStatusType>([
  SignalStatus.POTENTIAL_VULNERABILITY,
  SignalStatus.CONFIRMED_VULNERABILITY,
]);

export const FALSE_POSITIVE_NEW_STATUSES = new Set<SignalStatusType>([
  SignalStatus.DISPROVEN_VULNERABILITY,
]);

export const STATUSES_REQUIRING_RELATED_FINDING_DELETE_CONFIRM =
  new Set<SignalStatusType>([
    SignalStatus.DISPROVEN_VULNERABILITY,
    SignalStatus.DUPLICATE,
  ]);

export const shouldConfirmDeleteRelatedFinding = ({
  newStatus,
  relatedFinding,
}: {
  newStatus: SignalStatusType | null;
  relatedFinding?: SignalRelatedFindingRef;
}): boolean =>
  !!relatedFinding &&
  newStatus !== null &&
  STATUSES_REQUIRING_RELATED_FINDING_DELETE_CONFIRM.has(newStatus);

export const resolveStageForStatusUpdate = ({
  currentStage,
  newStatus,
}: {
  currentStage: SignalStageType;
  newStatus: SignalStatusType | null;
}): SignalStageType => {
  const defaultStage = defaultStageForSignalStatus({ status: newStatus });

  const confirmedFitsCurrentReviewStage =
    newStatus === SignalStatus.CONFIRMED_VULNERABILITY &&
    defaultStage !== currentStage &&
    currentStage !== SignalStage.IN_TESTING &&
    isAllowedStageStatusCombination({ stage: currentStage, status: newStatus });

  return confirmedFitsCurrentReviewStage ? currentStage : defaultStage;
};

export const isFailureAreaStatusChange = ({
  previousStatus,
  newStatus,
}: {
  previousStatus: SignalStatusType | null;
  newStatus: SignalStatusType | null;
}): boolean => {
  if (!previousStatus || !newStatus) {
    return false;
  }

  if (
    FALSE_POSITIVE_PREVIOUS_STATUSES.has(previousStatus) &&
    newStatus === SignalStatus.DISPROVEN_VULNERABILITY
  ) {
    return true;
  }

  if (
    previousStatus === SignalStatus.DISPROVEN_VULNERABILITY &&
    newStatus === SignalStatus.CONFIRMED_VULNERABILITY
  ) {
    return true;
  }

  return false;
};

export const resolveReportFalsePositive = ({
  currentStatus,
  pendingStatus,
  markedAsFalsePositive,
}: {
  currentStatus: SignalStatusType | null;
  pendingStatus: SignalStatusType | null | undefined;
  markedAsFalsePositive: boolean;
}): boolean | undefined => {
  if (pendingStatus !== SignalStatus.DISPROVEN_VULNERABILITY) {
    return undefined;
  }

  if (currentStatus === SignalStatus.CONFIRMED_VULNERABILITY) {
    return true;
  }

  if (
    currentStatus === SignalStatus.POTENTIAL_VULNERABILITY &&
    markedAsFalsePositive
  ) {
    return true;
  }

  return undefined;
};
